import React from 'react';

const frameworks = [
  {
    name: 'GDPR',
    description: 'DPAs on every engagement. EU data residency available on request.',
  },
  {
    name: 'HIPAA',
    description: 'BAA-covered vendors only. We sign as business associate where required.',
  },
  {
    name: 'SOC 2',
    description: 'Controls follow Trust Services Criteria. No audit report is claimed, but controls are in place.',
  },
  {
    name: 'EU AI Act',
    description: 'Risk classification, transparency, human oversight and technical documentation per system.',
  },
  {
    name: 'ISO 27001',
    description: 'Mapped to Annex A controls. No certification claimed.',
  },
  {
    name: 'CCPA/CPRA',
    description: 'Consumer data rights, opt-out and deletion for US-based data subjects.',
  },
];

const controls = [
  {
    title: 'Data in transit and at rest',
    description:
      'TLS 1.2 or higher in transit. AES-256 at rest. PII and PHI redacted before prompts leave your environment. EU and US data residency options. Retention enforced by policy.',
  },
  {
    title: 'Models and outputs',
    description:
      'No training on your data. Enterprise and zero-retention endpoints with OpenAI, Anthropic, Google, AWS Bedrock and Azure. Input and output guardrails. Eval suite before every release. You can swap or move models at any time.',
  },
  {
    title: 'Access and oversight',
    description:
      'Least-privilege access, SSO and RBAC. Full audit trail on every action. Human-in-the-loop on consequential decisions. Incident response per GDPR timelines.',
  },
  {
    title: 'What you get under NDA',
    description:
      'Data processing agreement, BAA where needed, security architecture and data-flow diagrams, subprocessor list, EU AI Act technical documentation per system.',
  },
];

const Trust: React.FC = () => {
  return (
    <>
      <section className="section-padding bg-white">
        <div className="container-max">
          <p className="text-sm text-neutral-500 mb-3">Trust Center</p>
          <h1 className="text-4xl md:text-5xl font-semibold text-black leading-[1.05] tracking-[-0.02em] mb-5 max-w-3xl">
            Your data stays yours. Every system proves it.
          </h1>
          <p className="text-lg text-neutral-600 leading-relaxed max-w-2xl">
            We build AI for teams handling financial, health and personal data. Here is how we protect it.
          </p>
        </div>
      </section>

      <section className="section-padding bg-neutral-50">
        <div className="container-max">
          <h2 className="text-3xl font-semibold text-black tracking-tight mb-10">
            Framework alignment
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {frameworks.map((f) => (
              <div key={f.name} className="bg-white rounded-2xl p-6 border border-neutral-200 flex gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-base font-semibold text-black">{f.name}</h3>
                    <span className="inline-flex items-center text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full">
                      Aligned
                    </span>
                  </div>
                  <p className="text-neutral-600 text-sm leading-relaxed">{f.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-max">
          <h2 className="text-4xl md:text-5xl font-semibold text-black leading-[1.05] tracking-[-0.02em] mb-10">
            What we control
          </h2>
          <div className="space-y-8 max-w-3xl">
            {controls.map((c, i) => (
              <div key={c.title} className="flex gap-6">
                <span className="text-sm font-medium text-neutral-300 shrink-0 w-6 pt-1">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-black mb-2">{c.title}</h3>
                  <p className="text-neutral-600 leading-relaxed">{c.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-neutral-50">
        <div className="container-max">
          <p className="text-lg text-neutral-600 mb-6">Questions about security or compliance?</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="mailto:contact@zwiftlabs.com"
              className="inline-flex items-center bg-white hover:bg-neutral-50 text-black border border-neutral-200 hover:border-neutral-900 font-medium px-7 py-3.5 rounded-full transition-colors"
            >
              Email contact@zwiftlabs.com
            </a>
            <a
              href="https://calendar.app.google/cnN9KoLp6WZYMX9G7"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-black hover:bg-neutral-800 text-white font-medium px-7 py-3.5 rounded-full transition-colors"
            >
              Book a fit call
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Trust;
